import React, { useState } from 'react';
import FilterInput from './FilterInput';
import { Button, Menu, MenuItem, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import exportIcon from '../../assets/icons/export.svg';
import csvIcon from '../../assets/icons/csv.svg';
import pdfIcon from '../../assets/icons/pdf.svg';

const Filter = ({ 
  filters = [], 
  filterValues = {}, 
  onFilterChange, 
  onReset, 
  showApplyButton = false,
  onApply,
  onExport,
  exportFormats = ['CSV', 'PDF'],
  columns = 'auto', // New prop for controlling columns
  hasActiveFilters = false,
  hasSelection = false,
  hasResults = true,
  onDatePickerOpenChange
}) => {
  // Count active filters
  const activeFiltersCount = Object.entries(filterValues).filter(([key, value]) => {
    if (value === null || value === undefined || value === '') return false;
    
    // Find the filter configuration to check if it's multi-select
    const filterConfig = filters.find(f => f.name === key);
    const isMultiSelect = filterConfig?.isMultiSelect || false;
    
    if (isMultiSelect) {
      // For multi-select, check if array exists and has items
      return Array.isArray(value) && value.length > 0;
    }
    
    // For date range objects, check if both start and end are not null
    if (typeof value === 'object' && value !== null) {
      // Check if it's a date range object with start and end properties
      if ('start' in value && 'end' in value) {
        return value.start !== null && value.end !== null;
      }
      // For other objects, return false (empty objects)
      return false;
    }
    
    return true;
  }).length;
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const handleExportClick = (event) => {
    // Don't open menu if no results to export
    if (!hasResults) {
      return;
    }
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCSV = () => {
    onExport && onExport('CSV');
    handleExportClose();
  };

  const handleExportPDF = () => {
    onExport && onExport('PDF');
    handleExportClose();
  };
  const renderFilterField = (filter) => {
    // Check if this filter should be multi-select based on filter configuration
    const isMultiSelect = filter.isMultiSelect || false;
    const hasValue = filterValues[filter.name] && 
      (isMultiSelect 
        ? (Array.isArray(filterValues[filter.name]) && filterValues[filter.name].length > 0)
        : (typeof filterValues[filter.name] !== 'object' || 
           (filterValues[filter.name].start !== null || filterValues[filter.name].end !== null)));
    
    return (
      <FilterInput
        key={filter.name}
        type={filter.type || 'text'}
        label={filter.label}
        name={filter.name}
        options={filter.options || []}
        value={filterValues[filter.name] || (isMultiSelect ? [] : '')}
        onChange={(value) => onFilterChange(filter.name, value)}
        onClear={() => onFilterChange(filter.name, isMultiSelect ? [] : null)}
        placeholder={filter.placeholder}
        required={filter.required}
        hasValue={hasValue}
        isMultiSelect={isMultiSelect}
        isSearchable={filter.isSearchable}
        highlightOptionValues={filter.highlightOptionValues}
        onDatePickerOpenChange={(filter.type === 'dateRange' || filter.type === 'daterange') ? onDatePickerOpenChange : undefined}
      />
    );
  };

  return (
    <div className="flex flex-col gap-2" style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
      <div className="flex justify-end items-center gap-4">
        {activeFiltersCount > 0 && (
          <Box
            sx={{
              backgroundColor: '#007BC6',
              color: '#ffffff',
              borderRadius: '16px',
              px: 2,
              py: 0.5,
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              touchAction: 'manipulation',
              '&:hover': {
                backgroundColor: '#0265A3',
              },
            }}
            onClick={onReset}
            onTouchEnd={(e) => { e.preventDefault(); onReset(); }}
          >
            <span>{activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''}</span>
            <CloseIcon sx={{ fontSize: '16px' }} />
          </Box>
        )}
        <span
          role="button"
          tabIndex={0}
          style={{
            color: '#007BC6',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'Montserrat, sans-serif',
            cursor: 'pointer',
            textDecoration: 'underline',
            touchAction: 'manipulation',
          }}
          onClick={onReset}
          onTouchEnd={(e) => { e.preventDefault(); onReset(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onReset(); } }}
        >
          Réinitialiser
        </span>
      </div>

      <div>
        {/* Single Row - Clean Layout */}
        <div 
          className={columns === 'auto' ? "flex flex-wrap gap-4 items-center" : "grid gap-4 items-center"}
          style={columns !== 'auto' ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : {}}
        >
          {filters.map((filter) => renderFilterField(filter))}
          
          {/* Export Button - Only show if onExport is provided */}
          {onExport && (
            <Button
              variant="outlined"
              onClick={handleExportClick}
              onTouchEnd={(e) => { e.preventDefault(); if ((hasActiveFilters || hasSelection) && hasResults) handleExportClick(e); }}
              disabled={(!hasActiveFilters && !hasSelection) || !hasResults}
              sx={{
                backgroundColor: (!hasActiveFilters && !hasSelection) || !hasResults ? '#E5E5E5' : '#ffffff',
                border: 'none',
                borderRadius: '12px',
                width: '48px',
                height: '48px',
                minWidth: '48px',
                padding: '0',
                boxShadow: '0px 20px 80px #00000011',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (!hasActiveFilters && !hasSelection) || !hasResults ? 0.6 : 1,
                cursor: (!hasActiveFilters && !hasSelection) || !hasResults ? 'not-allowed' : 'pointer',
                '&:hover': {
                  backgroundColor: (!hasActiveFilters && !hasSelection) || !hasResults ? '#E5E5E5' : '#F8F9FA',
                },
              }}
            >
              <img 
                src={exportIcon} 
                alt="Export" 
                style={{ 
                  width: '20px', 
                  height: '20px',
                  filter: (!hasActiveFilters && !hasSelection) || !hasResults
                    ? 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(60%) contrast(100%)'
                    : 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(200deg) brightness(104%) contrast(97%)'
                }} 
              />
            </Button>
          )}
        </div>
      </div>

      {/* Export Menu - Only show if onExport is provided */}
      {onExport && (
        <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #E3E3E3',
            mt: 1,
          }
        }}
      >
        {exportFormats.includes('CSV') && (
          <MenuItem 
            onClick={handleExportCSV}
            onTouchEnd={(e) => { e.preventDefault(); handleExportCSV(); }}
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: 'Montserrat, sans-serif',
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 16px',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }}
          >
            <img src={csvIcon} alt="CSV" style={{ width: '20px', height: '20px' }} />
            Exporter CSV
          </MenuItem>
        )}
        {exportFormats.includes('PDF') && (
          <MenuItem 
            onClick={handleExportPDF}
            onTouchEnd={(e) => { e.preventDefault(); handleExportPDF(); }}
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              fontFamily: 'Montserrat, sans-serif',
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 16px',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }}
          >
            <img src={pdfIcon} alt="PDF" style={{ width: '20px', height: '20px' }} />
            Exporter PDF
          </MenuItem>
        )}
        </Menu>
      )}
    </div>
  );
};

export default Filter;